import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7271/api';

// Enums
export enum SpecialPaymentStatus {
  Pending = 1,
  Paid = 2,
  Cancelled = 3
}

export enum PaymentMethod {
  Cash = 1,
  BankTransfer = 2,
  Check = 3,
  CreditCard = 4,
  DirectDebit = 5,
  Other = 6
}

// Types
export interface SpecialPaymentDto {
  id: number;
  clientId: number;
  clientName: string;
  amount: number;
  description: string;
  status: number;
  statusName: string;
  paymentMethod?: number;
  paymentMethodName?: string;
  paymentDate?: string;
  comments?: string;
  createdBy: number;
  createdByName: string;
  updatedBy?: number;
  updatedByName?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface CreateSpecialPaymentDto {
  clientId: number;
  amount: number;
  description: string;
  comments?: string;
}

export interface UpdateSpecialPaymentDto {
  amount: number;
  description: string;
  status: number;
  paymentMethod?: number;
  paymentDate?: string;
  comments?: string;
}

// API
export const specialPaymentsApi = {
  getById: async (id: number): Promise<SpecialPaymentDto> => {
    const response = await axios.get(`${API_BASE_URL}/specialpayments/${id}`);
    return response.data;
  },

  getByClientId: async (clientId: number): Promise<SpecialPaymentDto[]> => {
    const response = await axios.get(`${API_BASE_URL}/specialpayments/client/${clientId}`);
    return response.data;
  },

  create: async (dto: CreateSpecialPaymentDto): Promise<SpecialPaymentDto> => {
    const response = await axios.post(`${API_BASE_URL}/specialpayments`, dto);
    return response.data;
  },

  update: async (id: number, dto: UpdateSpecialPaymentDto): Promise<SpecialPaymentDto> => {
    const response = await axios.put(`${API_BASE_URL}/specialpayments/${id}`, dto);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`${API_BASE_URL}/specialpayments/${id}`);
  }
};

// Helper functions
export const getStatusBadgeColor = (status: number): string => {
  switch (status) {
    case SpecialPaymentStatus.Paid:
      return '#16a34a';
    case SpecialPaymentStatus.Pending:
      return '#d97706';
    case SpecialPaymentStatus.Cancelled:
      return '#dc2626';
    default:
      return 'var(--text-secondary)';
  }
};

export const getPaymentMethodName = (method: number): string => {
  switch (method) {
    case PaymentMethod.Cash:
      return 'מזומן';
    case PaymentMethod.BankTransfer:
      return 'העברה בנקאית';
    case PaymentMethod.Check:
      return 'צ\'ק';
    case PaymentMethod.CreditCard:
      return 'כרטיס אשראי';
    case PaymentMethod.DirectDebit:
      return 'הוראת קבע';
    case PaymentMethod.Other:
      return 'אחר';
    default:
      return 'לא ידוע';
  }
};

export const formatCurrency = (amount: number): string => {
  return `₪${amount.toLocaleString('he-IL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

export const handleApiError = (error: any): string => {
  if (error.response) {
    return error.response.data || 'שגיאה בביצוע הפעולה';
  } else if (error.request) {
    return 'שגיאה בחיבור לשרת';
  } else {
    return 'שגיאה לא צפויה';
  }
};
